import { GridColumnGroupingModel } from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import React from "react";
import { PlanId } from "../boundary/PlanId";
import { DRITotalConsumptionCell } from "./DRITotalConsumptionCell";
import { InventoryCell } from "./InventoryCell";
import {
  bottomEmptyGroupColumnHeaderClassName,
  bottomGroupColumnRowHeaderClassName,
  emptyGroupColumnHeaderClassName,
  groupColumnCellClassName,
  groupColumnHeaderClassName,
  groupColumnRowHeaderClassName,
  groupLeftColumnHeaderClassName,
} from "./sx";
import { TotalConsumptionCell } from "./TotalConsumptionCell";

interface Material {
  id: number;
  name: string;
  inventory: number | null;
}

export const useColumnGroupingModel = (
  planId: PlanId,
  period: Period,
  massScaleFactor: number,
  materials: Material[],
  driInventory: number | null,
  isPercentageMode = false,
  showInventory = true,
  hasNameOverride = false
) => {
  const { t } = useTenantTranslation();
  const inventoryScaleFactor = isPercentageMode ? 1 : massScaleFactor;

  const columnGroupingModel = React.useMemo(() => {
    return [
      {
        groupId: "0",
        headerClassName: emptyGroupColumnHeaderClassName.toString(),
        children: [
          {
            groupId: "1",
            renderHeaderGroup: () => null,
            headerClassName: bottomEmptyGroupColumnHeaderClassName.toString(),
            children: [
              {
                field: "mix_name",
              },
              {
                field: "num_heats",
              },
              {
                field: "steel_grades",
              },
              {
                field: "grouping",
              },
              ...(hasNameOverride ? [{ field: "name_override" }] : []),
            ],
          },
        ],
      },
      ...(driInventory === null || !showInventory
        ? driInventory === null
          ? []
          : [
              {
                groupId: "dri_weight_inventory",
                headerClassName: emptyGroupColumnHeaderClassName.toString(),
                renderHeaderGroup: () => null,
                children: [
                  {
                    groupId: "dri_weight_consumption",
                    headerClassName:
                      bottomEmptyGroupColumnHeaderClassName.toString(),
                    renderHeaderGroup: () => null,
                    children: [{ field: "dri_weight" }],
                  },
                ],
              },
            ]
        : [
            {
              groupId: "dri_weight_inventory",
              headerName: driInventory.toFixed(0),
              renderHeaderGroup: () => (
                <InventoryCell amount={driInventory * inventoryScaleFactor} />
              ),
              headerClassName: groupLeftColumnHeaderClassName.toString(),
              children: [
                {
                  groupId: "dri_weight_consumption",
                  headerName: "consumed",
                  renderHeaderGroup: () => (
                    <DRITotalConsumptionCell
                      inventoryAmount={driInventory}
                      planId={planId}
                      period={period}
                      massScaleFactor={inventoryScaleFactor}
                    />
                  ),
                  headerClassName: groupColumnCellClassName.toString(),
                  children: [{ field: "dri_weight" }],
                },
              ],
            },
          ]),
      ...(showInventory
        ? [
            {
              groupId: "inventory",
              headerName: t("usableInventory"),
              headerClassName: groupColumnRowHeaderClassName.toString(),
              children: [
                {
                  groupId: t("consumption"),
                  headerClassName:
                    bottomGroupColumnRowHeaderClassName.toString(),
                  children: [{ field: "basket_weight" }],
                },
              ],
            },
          ]
        : [
            {
              groupId: "inventory_placeholder",
              headerClassName: emptyGroupColumnHeaderClassName.toString(),
              renderHeaderGroup: () => null,
              children: [
                {
                  groupId: "consumption_placeholder",
                  headerClassName:
                    bottomEmptyGroupColumnHeaderClassName.toString(),
                  renderHeaderGroup: () => null,
                  children: [{ field: "basket_weight" }],
                },
              ],
            },
          ]),
      ...materials.map((material): GridColumnGroupingModel[0] =>
        showInventory
          ? {
              groupId: material.id.toString() + ".inventory",
              headerName: material.inventory?.toString() ?? "N/A",
              renderHeaderGroup: () => (
                <InventoryCell
                  amount={(material.inventory ?? 0) * inventoryScaleFactor}
                />
              ),
              headerClassName: groupColumnHeaderClassName.toString(),
              children: [
                {
                  groupId: material.id.toString() + " consumption",
                  headerClassName: groupColumnCellClassName.toString(),
                  renderHeaderGroup: () => (
                    <TotalConsumptionCell
                      planId={planId}
                      period={period}
                      materialName={material.name}
                      inventoryAmount={material.inventory ?? 0}
                      massScaleFactor={inventoryScaleFactor}
                    />
                  ),
                  children: [
                    {
                      field: material.id.toString(),
                    },
                  ],
                },
              ],
            }
          : {
              groupId: material.id.toString() + ".inventory",
              headerClassName: emptyGroupColumnHeaderClassName.toString(),
              renderHeaderGroup: () => null,
              children: [
                {
                  groupId: material.id.toString() + " consumption",
                  headerClassName:
                    bottomEmptyGroupColumnHeaderClassName.toString(),
                  renderHeaderGroup: () => null,
                  children: [{ field: material.id.toString() }],
                },
              ],
            }
      ),
    ];
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- inventoryScaleFactor is derived from isPercentageMode and massScaleFactor which are already tracked; including it would cause duplicate rerenders
  }, [
    materials,
    driInventory,
    isPercentageMode,
    massScaleFactor,
    planId,
    period,
    t,
    showInventory,
    hasNameOverride,
  ]);
  return columnGroupingModel;
};
